import { Select as AntdSelect, Form, Tag } from "antd";
import React, { useCallback, useMemo, useState, useRef } from "react";
import LabelAndValue from "../../form/LabelAndValue";
import HInput from "../input/Input";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../../pages/utils/commonUtils";
import HButton from "../button/Button";
import { PlusOutlined } from "@ant-design/icons";

function HSelect(props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [hasError, setHasError] = useState(false);
  const {
    options,
    disabled,
    customProps,
    dropdownExtras,
    size,
    enableSearch = true,
    listEndComponent,
    label,
    searchPlaceHolder = customProps?.searchBoxPlaceHolder ?? "Search",
    placeholder = "Select",
    name,
    required,
    defaultValue,
    popupClassName = "customSelectPopup",
    groupBy,
    open,
    popupContainerId,
    mode,
    defaultTagRender,
    "data-cy": dataCy,
    customTagRender,
    enableAdd = false,
    onAdd = () => {},
    notFoundContent,
    popupMatchSelectWidth = true,
    dropdownRender = (menu) => {
      return (
        <DropdownContent
          dropdownExtras={dropdownExtras}
          enableSearch={enableSearch}
          searchPlaceHolder={searchPlaceHolder}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          enableAdd={enableAdd}
          onAdd={onAdd}
          menu={menu}
          listEndComponent={listEndComponent}
          optionListMaxHeight={optionListMaxHeight}
          notFoundContent={notFoundContent}
          options={options}
          size={size}
        />
      );
    },
    formItemRules = required
      ? [
          {
            required: true,
            validator: (_, value) => {
              if (!value && !customProps?.value) {
                setHasError(true);
                return Promise.reject(new Error("Required"));
              } else {
                setHasError(false);
                return Promise.resolve();
              }
            },
          },
        ]
      : [],
    handleDropDownVisibleChange = () => {},
    onMouseDown,
    showOnlyPopup,
    selectSource,
    loading = false,
    optionListMaxHeight,
    type = "primary",
    variant = "",
    floatingLabel = false,
    rootClassName = "",
  } = props;

  const wrapperRef = useRef(null);
  const labelText = placeholder || label || "";
  const isBorderless = variant === "borderless";
  const showFloatingLabel = floatingLabel && !!labelText && !showOnlyPopup && variant === "underlined";

  const handleBeforeDropDownVisibleChange = (visible) => {
    if (!visible) {
      setSearchTerm("");
    }
    handleDropDownVisibleChange(visible);
  };

  const filterFromString = (stringToBeFiltered, searchTerm) => {
    if (typeof stringToBeFiltered === "string" && typeof searchTerm === "string")
      return stringToBeFiltered?.toLowerCase().includes(searchTerm?.toLowerCase());
  };

  const filteredOptions = useMemo(() => {
    return searchTerm
      ? options?.filter((option) => {
          let stringToFilter = "";
          if (typeof option?.label === "string") {
            stringToFilter = option?.labelText ?? option?.label;
          } else {
            stringToFilter = option?.labelText ?? option?.label?.props?.children;
          }
          return filterFromString(stringToFilter, searchTerm);
        })
      : options;
  }, [options, searchTerm]);

  const mapOptions = useCallback(
    (listOfOptions) => {
      if (!listOfOptions) return [];

      listOfOptions = listOfOptions.map((option) =>
        typeof option === "string"
          ? {
              ...option,
              label: (
                <div className="truncate-text" data-cy={`${option?.dataCy}`}>
                  {capitalizeFirstLetter(option)}
                </div>
              ),
              value: option,
            }
          : {
              ...option,
              label: (
                <div className="truncate-text" data-cy={`${option?.dataCy}`}>
                  {option?.label}
                </div>
              ),
              value: option?.value,
            }
      );

      if (!groupBy) return listOfOptions;

      const groupedItems = {};

      // Group items by category
      listOfOptions.forEach((option) => {
        const groupByKey = option[groupBy] ?? "Unknown";
        if (!groupedItems[groupByKey]) {
          groupedItems[groupByKey] = [];
        }
        groupedItems[groupByKey].push(option);
      });

      // Create the final list in the desired format
      const groupedOptions = Object.keys(groupedItems).map((category) => ({
        label: <Tag className="select-group-tag">{category}</Tag>,
        title: category,
        options: groupedItems[category],
      }));
      return groupedOptions;
    },
    [groupBy]
  );

  const customDefaultTagRender = (props) => {
    const { label, value, closable } = props;

    const onPreventMouseDown = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const handleTagRender = () => {
      if (defaultTagRender?.onCloseSetState) {
        defaultTagRender.onCloseSetState((prev) => (Array.isArray(prev) ? prev.filter((item) => item !== value) : []));
      }
      if (defaultTagRender?.onClose) {
        defaultTagRender.onClose(value);
      }
    };

    return (
      <Tag
        className="p-1 mb-1 mt-1 hs-border-15 hs-bg-tag-purple d-flex align-items-center hs-fw-500 user-select-tab"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={handleTagRender}
      >
        <span>{formatPhoneNumber(defaultTagRender?.tagItemToRender === "value" ? value : label)}</span>
      </Tag>
    );
  };

  const tagRender = customDefaultTagRender || customTagRender;

  const variantClass = variant === "borderless" ? "hs-input-borderless" : variant === "underlined" ? "hs-input-underlined" : "hs-input-box";

  return (
    <LabelAndValue label={label} showOnlyPopup={showOnlyPopup} hasError={hasError}>
      <div ref={wrapperRef} className={isBorderless ? "" : "hs-input-floating-label-wrapper"}>
        {showFloatingLabel && (
          <LabelAndValue floating={true} label={labelText} hasError={hasError} labelClass="hs-input-floating-label">
            <></>
          </LabelAndValue>
        )}
        <Form.Item name={name} rules={formItemRules} className="mb-0 h-100">
          <AntdSelect
            {...(customProps || {})}
            mode={mode}
            size={size}
            value={customProps?.value}
            suffixIcon={
              showOnlyPopup ? (
                <></>
              ) : loading ? (
                <div className="actionIconsSprite dropDownLoading" />
              ) : (
                customProps?.suffixIcon || <div className="actionIconsSprite fromNumberDropDownIcon" />
              )
            }
            getPopupContainer={() => document.getElementById(popupContainerId)}
            open={open}
            defaultValue={defaultValue}
            fieldNames={name}
            options={mapOptions(filteredOptions)}
            popupMatchSelectWidth={popupMatchSelectWidth}
            data-cy={dataCy}
            className={`${
              showOnlyPopup
                ? "hs-select-show-only-popup"
                : `${size ? "" : "hs-h-40-px"} hs-fs-14  hs-placeholder-text ${customProps?.className ?? ""}  ${
                    hasError ? "hs-input-has-error" : ""
                  }${showFloatingLabel ? `${variantClass} hs-input-with-floating-label` : variantClass} ${
                    isBorderless ? "hs-select-borderless" : ""
                  }`
            }`}
            filterOption={false}
            popupClassName={popupClassName}
            dropdownRender={dropdownRender}
            onDropdownVisibleChange={handleBeforeDropDownVisibleChange}
            placeholder={showFloatingLabel ? "" : placeholder}
            onMouseDown={onMouseDown}
            tagRender={tagRender}
            disabled={disabled}
            onChange={(val, option) => {
              if (props?.onChange) {
                props?.onChange?.(val, option);
              } else if (customProps?.onChange) {
                customProps?.onChange?.(val, option);
              }
            }}
            notFoundContent={
              <div className="dropDownList" style={{ height: 40 }}>
                <div className="d-flex justify-content-start align-items-center h-100">
                  <span className="hs-color-md-grey">{notFoundContent ?? `No ${selectSource || "options"}!`}</span>
                </div>
              </div>
            }
            rootClassName={rootClassName}
          ></AntdSelect>
        </Form.Item>
      </div>
    </LabelAndValue>
  );
}

export default HSelect;

const DropdownContent = ({
  dropdownExtras,
  enableSearch,
  searchPlaceHolder,
  searchTerm,
  setSearchTerm,
  enableAdd,
  onAdd,
  menu,
  listEndComponent,
  optionListMaxHeight,
  notFoundContent,
  options,
  dropDown,
  size,
  popupMatchSelectWidth,
}) => {
  return (
    <div className="px-1 z-3">
      {dropdownExtras && dropdownExtras()}
      {enableSearch && (
        <div className="d-flex mb-1">
          <HInput
            size="xs"
            type="search"
            data-cy="select-search-input"
            placeholder={searchPlaceHolder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="selectBoxSearchInput"
            onKeyDown={(e) => {
              if (e.key === "Enter" && enableAdd && searchTerm.length > 3) {
                onAdd(searchTerm);
                setSearchTerm("");
              }
            }}
            variant="underlined"
            floatingLabel={false}
          />
          {enableAdd && searchTerm.length > 3 && (
            <div className="d-flex align-items-center justify-content-center">
              <HButton
                onClick={() => {
                  onAdd(searchTerm);
                  setSearchTerm("");
                }}
                icon={<PlusOutlined />}
              >
                Add
              </HButton>
            </div>
          )}
        </div>
      )}
      {options?.length > 0 ? (
        <>
          {optionListMaxHeight ? (
            <div className="dropDownList" data-cy={dropDown} style={{ maxHeight: optionListMaxHeight, overflowY: "auto" }}>
              {menu}
            </div>
          ) : (
            <div className="dropDownList">{menu}</div>
          )}
        </>
      ) : (
        <div className="dropDownList" style={{ height: 40 }}>
          <div className="d-flex justify-content-start align-items-center h-100">
            <span className="hs-color-md-grey">{notFoundContent}</span>
          </div>
        </div>
      )}

      {listEndComponent && <div>{listEndComponent}</div>}
    </div>
  );
};
